import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Hammer, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useMmText } from "../../hooks/useMmText";
import {
  aiTargetState,
  applyOperator,
  changeStatus,
  closeVisit,
  createTemplate,
  getMastery,
  openVisit,
  snapshotProfile,
  writebackPreview,
  type MmMenu,
  type MmObject,
  type MmProfile,
  type MmRegister,
  type MmVisit as MmVisitRecord,
  type MmVisitReason,
} from "../../api/mentalModels";
import MmProfilePanel from "./MmProfilePanel";
import MmOperatorFlow from "./MmOperatorFlow";
import MmPathPicker from "./MmPathPicker";
import MmPathEditor from "./MmPathEditor";

export interface MmVisitGoal {
  statedOutcome?: string;
  targetState?: MmProfile;
  pathId?: string;
}

export interface MmWriteback {
  objectId: string;
  current: string;
  proposed: string;
}

export type MmVisitEditor = "goal" | "path" | null;

interface Session {
  object: MmObject;
  visit: MmVisitRecord;
  menu: MmMenu;
  warnings: string[];
  /** ICDF §2 — set when the visit was opened with a reason */
  reasonStats?: { frictionVisits: number };
  reasonKind?: "friction" | "plan";
}

interface Props {
  object: MmObject;
  register: MmRegister;
  /** ICDF §2 — why the extended brain was opened (undefined = "Just open it"). */
  reason?: MmVisitReason;
  goal?: MmVisitGoal;
  /** The page's quiet links open the goal editor / path picker in the visit. */
  editor: MmVisitEditor;
  onEditorChange: (editor: MmVisitEditor) => void;
  /** The object changed during the visit (re-rating, a move). */
  onObjectChange?: (object: MmObject) => void;
  /** "Done for now" (or park / retire): the visit closed. */
  onEnd: (result: { writeback?: MmWriteback }) => void;
  /** Open this operator first (Dive deeper "Short on time?", spec 2026-11-04 §2.3). */
  startOperator?: string;
}

/**
 * One visit with a mental model (moved out of MmStudio's session view, spec
 * 2026-11-03 model page §A3): opens the visit on mount, then the stepper, the
 * goal / path editors, the operate → re-profile loop and "Done for now".
 * API calls and events are the studio's, unchanged.
 */
const MmVisit: React.FC<Props> = ({
  object,
  register,
  reason,
  goal,
  editor,
  onEditorChange,
  onObjectChange,
  onEnd,
  startOperator,
}) => {
  const { t, i18n } = useTranslation();
  const { tm } = useMmText(register);
  const retryFetch = useRetryFetch();
  const language = i18n.language?.split("-")[0] || "en";

  const [session, setSession] = useState<Session | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Continuation opens (goal change, path pick) inherit the visit's reason
  const lastReasonRef = useRef<MmVisitReason | undefined>(reason);

  // ICDF §4 — operators this person has never applied get a "new to you" tag
  const [newOperatorIds, setNewOperatorIds] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    getMastery(retryFetch)
      .then((res) => {
        if (live) setNewOperatorIds(res.operators.filter((m) => m.level === "new").map((m) => m.operatorId));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [goalDraft, setGoalDraft] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [buildingPath, setBuildingPath] = useState(false);
  // Bumped after template changes so path pickers remount with fresh data
  const [pathsVersion, setPathsVersion] = useState(0);
  // Operator requested via a stepper chip click (consumed by MmOperatorFlow)
  const [requestedOperator, setRequestedOperator] = useState<string | null>(startOperator ?? null);

  const [delta, setDelta] = useState<{ before: MmProfile; after: MmProfile } | null>(null);
  const [emittedNotes, setEmittedNotes] = useState<string[]>([]);
  // Post-operator re-rating (design §4: operate → re-profile)
  const [rerate, setRerate] = useState<{ before: MmProfile; draft: MmProfile } | null>(null);

  // The BE visit this component holds open. Leaving without "Done for now"
  // (another model, closing the pane, switching tab) abandons it.
  const openVisitIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const closeHeld = (status: "completed" | "abandoned") => {
    const id = openVisitIdRef.current;
    openVisitIdRef.current = null;
    return id ? closeVisit(retryFetch, id, status) : Promise.resolve(undefined);
  };
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      closeHeld("abandoned").catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSession = async (obj: MmObject, g?: MmVisitGoal) => {
    setBusy(true);
    setFailed(false);
    try {
      const res = await openVisit(retryFetch, {
        fiObject: obj._id,
        register,
        reason: lastReasonRef.current,
        ...(g || {}),
      });
      if (!mountedRef.current) {
        // Opened after the page moved on: don't leave it open on the server
        closeVisit(retryFetch, res.visit._id, "abandoned").catch(() => {});
        return;
      }
      openVisitIdRef.current = res.visit._id;
      setDelta(null);
      setEmittedNotes([]);
      setRerate(null);
      setSavingTemplate(false);
      setBuildingPath(false);
      setSession({
        object: obj,
        visit: res.visit,
        menu: res.menu,
        warnings: res.warnings,
        reasonStats: res.reasonStats,
        reasonKind: lastReasonRef.current?.kind,
      });
    } catch (e) {
      console.error(e);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  // Open once per mount (a ref, so a StrictMode double effect can't open two)
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    openSession(object, goal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The goal editor starts from the visit's current outcome
  useEffect(() => {
    if (editor === "goal") setGoalDraft(session?.visit.statedOutcome || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const updateSession = (next: Session) => {
    setSession(next);
    if (next.object !== session?.object) onObjectChange?.(next.object);
  };

  const handleApply = async (args: { operatorId: string; input?: any; output?: any }) => {
    if (!session) return;
    const view = session;
    setBusy(true);
    try {
      const res = await applyOperator(retryFetch, view.visit._id, {
        ...args,
        proposedBy: "user",
      });
      if ((res as any).fault) {
        // guard rejection — refresh menu from response if present
        if ((res as any).menu) {
          setSession({ ...view, menu: (res as any).menu });
        }
        return;
      }
      const notes: string[] = [];
      // Surface the operator's own output first — the user should see what
      // their move produced, not only its side-effects
      const out = args.output || {};
      const outSummary =
        out.chosenName ||
        out.chosenFrame ||
        out.probe ||
        out.firstStep ||
        out.synthesis ||
        out.problem ||
        (typeof out.response === "string" ? out.response : "");
      if (outSummary) {
        notes.push(
          `${tm(`op_${args.operatorId}_title`, args.operatorId)}: ${String(outSummary).slice(0, 140)}`
        );
      }
      (res.emitted || []).forEach((a) => {
        notes.push(
          a.artifactType === "insight"
            ? tm("emitted_insight", "An insight was saved to your workspace.")
            : tm("emitted_takeaway", "A commitment was added to your Action Items.")
        );
      });
      if (res.children && res.children.length) {
        notes.push(
          `${res.children.length} ${tm("emitted_children", "new thought(s) were created from the parts — find them in the list.")}`
        );
      }
      setEmittedNotes(notes);
      setDelta(null);
      // The operate → re-profile loop: ask how it sits now, before the next move
      setRerate({ before: res.delta.before, draft: { ...res.object.profile } });
      // Advance planned route locally
      const plannedRoute = view.visit.plannedRoute.map((s) =>
        s.operatorId === args.operatorId && s.status === "pending"
          ? { ...s, status: "done" as const }
          : s
      );
      updateSession({
        ...view,
        object: res.object,
        visit: { ...view.visit, plannedRoute },
        menu: res.menu,
        warnings: res.warnings,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const saveRerate = async () => {
    if (!session || !rerate) return;
    const view = session;
    setBusy(true);
    try {
      const saved = await snapshotProfile(retryFetch, view.object._id, {
        profile: rerate.draft,
        trigger: "post_operator",
        fiVisit: view.visit._id,
      });
      setDelta({ before: rerate.before, after: saved.object.profile });
      setRerate(null);
      // Drop profile-based guard warnings the re-rating just invalidated
      // (the server recomputes the authoritative menu on the next apply)
      const p = saved.object.profile;
      const warnings = view.warnings.filter((w) => {
        if (w === "R2_examine_before_reframe") return (p.certainty ?? 3) >= 4;
        if (w === "R1_distance_first")
          return (p.charge ?? 0) <= -4 && (p.identification ?? 3) >= 4;
        return true;
      });
      updateSession({ ...view, object: saved.object, warnings });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const resetGoal = async () => {
    if (!session || !goalDraft.trim()) return;
    const view = session;
    setBusy(true);
    try {
      const ts = await aiTargetState(retryFetch, {
        statedOutcome: goalDraft.trim(),
        fiObject: view.object._id,
        language,
        register,
      });
      // A goal change is a new change attempt: close this visit, open a fresh
      // one carrying the new outcome + target — the server persists it on the
      // object and returns the new plan.
      try {
        await closeHeld("completed");
      } catch {
        /* best-effort */
      }
      onEditorChange(null);
      const outcome = goalDraft.trim();
      setGoalDraft("");
      await openSession(view.object, {
        statedOutcome: outcome,
        targetState: ts.targetState,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const endSession = async (status: "completed" | "abandoned") => {
    if (!session) return;
    const view = session;
    try {
      await closeHeld(status);
    } catch {
      /* closing is best-effort */
    }
    // Lifted object: propose updating the source entity (user-confirmed always)
    if (status === "completed" && view.object.liftedFrom?.entityType === "user_takeaway") {
      try {
        const preview = await writebackPreview(retryFetch, view.object._id);
        if (preview.hasChange) {
          onEnd({
            writeback: {
              objectId: view.object._id,
              current: preview.current,
              proposed: preview.proposed,
            },
          });
          return;
        }
      } catch {
        /* preview is optional */
      }
    }
    onEnd({});
  };

  const handleRetire = async (retiredNote: string) => {
    if (!session) return;
    setBusy(true);
    try {
      await changeStatus(retryFetch, session.object._id, { status: "retired", retiredNote });
      await endSession("completed");
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handlePark = async (parkedCondition: string) => {
    if (!session) return;
    setBusy(true);
    try {
      // Date-looking conditions become parkedUntil; anything else is an event condition
      const asDate = new Date(parkedCondition);
      const isDate = !isNaN(asDate.getTime()) && /\d/.test(parkedCondition);
      await changeStatus(retryFetch, session.object._id, {
        status: "parked",
        ...(isDate ? { parkedUntil: asDate.toISOString() } : { parkedCondition }),
      });
      await endSession("completed");
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const saveTemplate = async (name: string, steps: string[], objectTypes: string[], after: () => void) => {
    setBusy(true);
    try {
      await createTemplate(retryFetch, { name, operatorIds: steps, objectTypes });
      after();
      setPathsVersion((v) => v + 1);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (!session) {
    return failed ? (
      <div className="text-sm text-muted-foreground">
        <p role="alert">{tm("visit_failed", "Couldn't open this right now.")}</p>
        <button
          type="button"
          onClick={() => openSession(object, goal)}
          className="mt-1 min-h-[44px] text-primary hover:underline"
        >
          {tm("visit_retry", "Try again")}
        </button>
      </div>
    ) : (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("loading", "Loading…")}
      </div>
    );
  }

  const view = session;
  const route = view.visit.plannedRoute;

  return (
    <div className="space-y-4">
      {/* ICDF §2 — recurring friction is a signal, not just a statistic:
          examining hasn't settled this one; suggest building something. */}
      {view.reasonKind === "friction" && (view.reasonStats?.frictionVisits ?? 0) >= 3 && (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Hammer className="w-4 h-4 mt-0.5 flex-none text-primary/60" />
          <span>
            {tm(
              "friction_recurring",
              "Friction has brought you here {{count}} times now. When a thought keeps coming back, another look may not be enough — it might want a designed response. The design moves can build one."
            ).replace("{{count}}", String(view.reasonStats?.frictionVisits))}
          </span>
        </p>
      )}

      {editor === "goal" && (
        <div className="rounded-2xl border border-border p-4 space-y-2">
          <label className="text-sm font-medium" htmlFor="mm-visit-goal">
            {tm("goal_label", "What would you like to be different? (optional)")}
          </label>
          <input
            id="mm-visit-goal"
            value={goalDraft}
            onChange={(e) => setGoalDraft(e.target.value)}
            placeholder={tm("goal_placeholder", "e.g. stop dreading it and decide whether to do it")}
            className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={resetGoal} disabled={busy || !goalDraft.trim()}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("get_new_plan", "Get a new plan")}
            </Button>
            <button
              type="button"
              onClick={() => onEditorChange(null)}
              className="text-xs text-muted-foreground hover:text-foreground min-h-[44px] px-2"
            >
              {t("cancel", "Cancel")}
            </button>
          </div>
        </div>
      )}

      {editor === "path" && (
        <div className="rounded-2xl border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {tm("path_pick_label", "…or follow a proven path (optional)")}
            </span>
            <button
              type="button"
              onClick={() => onEditorChange(null)}
              className="text-xs text-muted-foreground hover:text-foreground min-h-[44px] px-2"
            >
              {t("cancel", "Cancel")}
            </button>
          </div>
          {buildingPath ? (
            <MmPathEditor
              register={register}
              busy={busy}
              onCancel={() => setBuildingPath(false)}
              onSave={(name, steps) =>
                saveTemplate(name, steps, [view.object.objectType], () => setBuildingPath(false))
              }
            />
          ) : (
            <MmPathPicker
              key={pathsVersion}
              objectType={view.object.objectType}
              register={register}
              compact
              onCreateNew={() => setBuildingPath(true)}
              onPick={async (p) => {
                try {
                  await closeHeld("completed");
                } catch {
                  /* best-effort */
                }
                onEditorChange(null);
                await openSession(view.object, { pathId: p.id });
              }}
            />
          )}
        </div>
      )}

      {editor === null && route.length > 0 && (
        <div className="rounded-2xl border border-border px-4 py-3">
          {view.visit.statedOutcome && (
            <p className="text-xs text-muted-foreground mb-2.5">
              {tm("plan_toward", "Toward:")}{" "}
              <span className="text-foreground/80 font-medium">“{view.visit.statedOutcome}”</span>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-y-2">
            {route.map((step, i) => {
              const done = step.status === "done";
              const isCurrent = !done && route.findIndex((s) => s.status !== "done") === i;
              const clickable = !done && view.menu.allowed.includes(step.operatorId);
              const Chip = clickable ? "button" : "span";
              return (
                <span key={`${step.operatorId}-${i}`} className="flex items-center">
                  {i > 0 && <span className={cn("h-px w-3 mx-0.5", done ? "bg-emerald-300" : "bg-border")} />}
                  <Chip
                    {...(clickable
                      ? {
                          onClick: () => setRequestedOperator(step.operatorId),
                          title: tm("stepper_start_hint", "Start this step"),
                        }
                      : {})}
                    className={cn(
                      "flex items-center gap-1.5 text-[11px] rounded-full ps-1 pe-2.5 py-0.5 border transition-colors motion-reduce:transition-none",
                      done
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300"
                        : isCurrent
                        ? "bg-primary/[0.07] border-primary/40 text-primary font-medium"
                        : "bg-muted/40 border-border/70 text-muted-foreground",
                      clickable &&
                        "cursor-pointer hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    )}
                  >
                    <span
                      className={cn(
                        "flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-semibold tabular-nums",
                        done
                          ? "bg-emerald-500 text-white"
                          : isCurrent
                          ? "bg-primary text-primary-foreground"
                          : "bg-border/80 text-muted-foreground"
                      )}
                    >
                      {done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                    </span>
                    {tm(`op_${step.operatorId}_title`, step.operatorId)}
                  </Chip>
                </span>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2.5 pt-2 border-t border-border/40">
            <button
              type="button"
              onClick={() => onEditorChange("goal")}
              className="text-[11px] text-primary/80 hover:text-primary"
            >
              {tm("change_goal", "change goal")}
            </button>
            <button
              type="button"
              onClick={() => setSavingTemplate(true)}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              {tm("save_as_template", "save as path")}
            </button>
          </div>
          {savingTemplate && (
            <div className="mt-3">
              <MmPathEditor
                register={register}
                initialSteps={route.map((s) => s.operatorId)}
                busy={busy}
                onCancel={() => setSavingTemplate(false)}
                onSave={(name, steps) =>
                  saveTemplate(name, steps, [view.object.objectType], () => setSavingTemplate(false))
                }
              />
            </div>
          )}
        </div>
      )}

      {emittedNotes.length > 0 && (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 text-sm space-y-1">
          {emittedNotes.map((note, i) => (
            <p key={i} className="flex items-center gap-1.5 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" /> {note}
            </p>
          ))}
        </div>
      )}

      {delta && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
          <p className="font-medium mb-1">{tm("delta_title", "Something moved:")}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
            {(["charge", "resolution", "certainty", "identification"] as const).map((attr) => {
              const b = delta.before[attr];
              const a = delta.after[attr];
              if (b === undefined || a === undefined || b === a) return null;
              return (
                <span key={attr} className="tabular-nums">
                  {tm(`attr_${attr}_short`, attr)}: {b} → <b>{a}</b>
                </span>
              );
            })}
            {(["charge", "resolution", "certainty", "identification"] as const).every(
              (attr) => delta.before[attr] === delta.after[attr]
            ) && <span>{tm("delta_none", "Ratings held steady — that's information too.")}</span>}
          </div>
        </div>
      )}

      {rerate ? (
        <div className="rounded-2xl border border-border p-4">
          <h4 className="text-sm font-semibold mb-3">{tm("rerate_title", "How does it sit with you now?")}</h4>
          <MmProfilePanel
            profile={rerate.draft}
            register={register}
            objectType={view.object.objectType}
            onChange={(attr, value) => setRerate({ ...rerate, draft: { ...rerate.draft, [attr]: value } })}
          />
          <Button className="mt-3" onClick={saveRerate} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
            {tm("rerate_save", "Save & continue")}
          </Button>
        </div>
      ) : (
        <MmOperatorFlow
          object={view.object}
          menu={view.menu}
          warnings={view.warnings}
          register={register}
          plannedRoute={route}
          applying={busy}
          requestOperator={requestedOperator}
          onRequestConsumed={() => setRequestedOperator(null)}
          newOperatorIds={newOperatorIds}
          pageLayout
          onApply={handleApply}
          onPark={handlePark}
          onRetire={handleRetire}
        />
      )}

      <div>
        <Button variant="outline" onClick={() => endSession("completed")} disabled={busy} className="min-h-[44px]">
          {tm("end_session", "Done for now")}
        </Button>
      </div>
    </div>
  );
};

export default MmVisit;
